import { initDefaultProps } from '../_util/props-util';
import { collapseConditionProps } from './commonProps';
import { ref, defineComponent, watch, onMounted } from 'vue';
import classNames from '../_util/classNames';
import useConfigInject from '../_util/hooks/useConfigInject';
import Icon from '../icon';
import Button from '../button';
import ResizeObserver from '../vc-resize-observer';
import LocaleReceiver from '../locale-provider/LocaleReceiver';
import isValid from '../_util/isValid';
export { collapseConditionProps };
export default defineComponent({
    compatConfig: { MODE: 3 },
    name: 'WdCollapseCondition',
    inheritAttrs: false,
    props: initDefaultProps(collapseConditionProps(), {
        open: false,
    }),
    slots: ['suffixButton'],
    setup(props, { attrs, slots, emit }) {
        const RenderConditionRef = ref();
        const RenderContentRef = ref();
        const RenderExpandRef = ref();
        const RenderButtonRef = ref();
        const { prefixCls } = useConfigInject('collapse-condition', props);
        const expandNodes = ref([]);
        const expandActive = ref(false);
        const getItems = () => slots.default?.();
        onMounted(() => {
            initActive();
        });
        watch(() => props.open, val => {
            RenderContentRef.value.style.height = val ? 'auto' : '32px';
        });
        watch(() => getItems(), () => {
            initActive();
        }, {
            flush: 'post', // https://cn.vuejs.org/guide/essentials/watchers.html#callback-flush-timing
        });
        const initActive = () => {
            const vnodes = getItems();
            const itemVnodes = vnodes.filter(item => item.children?.default);
            const renderConditionRect = RenderConditionRef.value.getBoundingClientRect();
            const renderButtonRect = RenderButtonRef.value.getBoundingClientRect();
            const renderExpandRect = RenderExpandRef.value.getBoundingClientRect();
            const suffixWidth = renderButtonRect.width + renderExpandRect.width;
            const contentMaxWidth = renderConditionRect.width - suffixWidth;
            let totalWidth = 0;
            expandActive.value = false;
            Array.prototype.forEach.call(itemVnodes, (item, i) => {
                const childrenNode = RenderContentRef.value.children[i];
                const { width } = childrenNode.getBoundingClientRect();
                totalWidth += width;
                if (totalWidth > contentMaxWidth) {
                    if (Array.isArray(item.props.value)) {
                        expandActive.value = item.props.value.length > 0 || expandActive.value;
                    }
                    else if (Object.prototype.toString.call(item.props.value) === '[object Object]') {
                        expandActive.value = Object.keys(item.props.value).length > 0 || expandActive.value;
                    }
                    else if (isValid(item.props.value)) {
                        expandActive.value = isValid(item.props.value) || expandActive.value;
                    }
                }
            });
            if (expandActive.value) {
                emit('update:open', true);
            }
        };
        const onClick = () => {
            const { open } = props;
            emit('update:open', !open);
        };
        const renderExpand = (locale) => {
            const { open } = props;
            return (<div ref={RenderExpandRef} class={`${prefixCls.value}-expand`}>
          <Button type={expandActive.value ? 'link' : 'text'} onClick={onClick}>
            <span>{open ? locale.collaspe : locale.expand}</span>
            <Icon iconClass={open ? 'wd-icon-direction_up' : 'wd-icon-direction_down'}/>
          </Button>
        </div>);
        };
        const renderButton = () => {
            const { suffixButton = slots?.suffixButton } = props;
            const items = getItems();
            const vnodes = items.filter(item => item.children?.default);
            const itemLen = vnodes?.length;
            const button = suffixButton && itemLen > 1 ? suffixButton() : null;
            return (<div ref={RenderButtonRef} class={`${prefixCls.value}-button`} v-show={!!button}>
          {button}
        </div>);
        };
        const handleResize = (size) => {
            if (!RenderButtonRef.value || !RenderExpandRef.value)
                return;
            const renderButtonRect = RenderButtonRef.value.getBoundingClientRect();
            const renderExpandRect = RenderExpandRef.value.getBoundingClientRect();
            const suffixWidth = renderButtonRect.width + renderExpandRect.width;
            const contentMaxWidth = size.width - suffixWidth;
            RenderContentRef.value.style.maxWidth = contentMaxWidth + 'px';
            let totalWidth = 0;
            expandActive.value = false;
            const items = getItems();
            const vnodes = items.filter(item => item.children?.default);
            expandNodes.value = [];
            Array.prototype.forEach.call(vnodes, (item, i) => {
                const childrenNode = RenderContentRef.value.children[i];
                const { width } = childrenNode.getBoundingClientRect();
                totalWidth += width;
                if (totalWidth > contentMaxWidth) {
                    expandNodes.value.push(childrenNode);
                    childrenNode.classList.remove(`${prefixCls.value}-panel-item-normal`);
                    childrenNode.classList.add(`${prefixCls.value}-panel-item-expand`);
                    childrenNode.style.visibility = 'hidden';
                    if (Array.isArray(item.props.value)) {
                        expandActive.value = item.props.value.length > 0 || expandActive.value;
                    }
                    else if (Object.prototype.toString.call(item.props.value) === '[object Object]') {
                        expandActive.value = Object.keys(item.props.value).length > 0 || expandActive.value;
                    }
                    else if (isValid(item.props.value)) {
                        expandActive.value = isValid(item.props.value) || expandActive.value;
                    }
                }
                else {
                    childrenNode.classList.remove(`${prefixCls.value}-panel-item-expand`);
                    childrenNode.classList.add(`${prefixCls.value}-panel-item-normal`);
                    childrenNode.style.visibility = 'visible';
                }
                if (props.open) {
                    childrenNode.style.visibility = 'visible';
                }
            });
            const normalNodes = RenderContentRef.value.querySelectorAll(`.${prefixCls.value}-panel-item-normal`);
            const width = Array.prototype.reduce.call(normalNodes, (prev, cur) => {
                return prev + cur.offsetWidth;
            }, 0);
            RenderContentRef.value.style.width = width + 'px';
            if (expandNodes.value.length > 0) {
                RenderExpandRef.value.style.visibility = 'visible';
            }
            else {
                RenderExpandRef.value.style.visibility = 'hidden';
            }
        };
        return () => {
            const { open } = props;
            const collapseClassName = classNames({
                [prefixCls.value]: true,
                [`${prefixCls.value}-open`]: open,
            });
            const contentStyle = {
                height: open ? 'auto' : '32px',
            };
            return (<LocaleReceiver componentName="CollaspeCondition" children={(locale) => (<ResizeObserver onResize={handleResize}>
              <div ref={RenderConditionRef} class={collapseClassName} style={attrs.style}>
                <div ref={RenderContentRef} class={`${prefixCls.value}-content`} style={contentStyle}>
                  {getItems()}
                </div>
                {renderButton()}
                {renderExpand(locale)}
              </div>
            </ResizeObserver>)}></LocaleReceiver>);
        };
    },
});
